import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'

interface Pricing {
  markup_mode: 'flat' | 'percent'
  markup_amount_usd: number
  markup_percent: number
  rate_preference: 'prefer_contract' | 'cheapest'
}

interface Props {
  pricing: Pricing
}

const EXAMPLE_BUY_USD = 2000

function formatUsd(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export default function SettingsPricing({ pricing }: Props) {
  const pathFor = useOrgPath()

  const { data, setData, patch, processing } = useForm({
    markup_mode: pricing.markup_mode || 'flat',
    markup_amount_usd: pricing.markup_amount_usd ? String(pricing.markup_amount_usd) : '',
    markup_percent: pricing.markup_percent ? String(pricing.markup_percent) : '',
    rate_preference: pricing.rate_preference || 'prefer_contract',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(pathFor('/settings/pricing'))
  }

  const isPercent = data.markup_mode === 'percent'
  const amount = Number(data.markup_amount_usd) || 0
  const percent = Number(data.markup_percent) || 0
  const exampleMarkup = isPercent ? (EXAMPLE_BUY_USD * percent) / 100 : amount
  const exampleSell = EXAMPLE_BUY_USD + exampleMarkup

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Settings</h1>
        <Button type="submit" form="pricing-form" size="sm" className="text-xs" disabled={processing}>
          Save Changes
        </Button>
      </header>

      <div className="max-w-2xl overflow-y-auto p-8">
        <form id="pricing-form" onSubmit={handleSubmit} className="space-y-8">
          <section>
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Client Markup
            </h2>
            <p className="mb-4 text-xs text-muted-foreground">
              Carrier rates are your cost (buy). This markup is added on top so clients are
              quoted the sell price, never your cost. Applied per container.
            </p>

            <div className="space-y-4">
              <div>
                <Label className="text-xs">Markup type</Label>
                <div className="mt-1 inline-flex rounded-md border p-0.5">
                  {([
                    { value: 'flat', label: 'Flat amount' },
                    { value: 'percent', label: 'Percentage' },
                  ] as const).map((option) => {
                    const active = data.markup_mode === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setData('markup_mode', option.value)}
                        className={`cursor-pointer rounded px-3 py-1 text-[13px] font-medium transition-colors ${
                          active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {isPercent ? (
                <div>
                  <Label htmlFor="markup_percent" className="text-xs">Markup percentage</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      id="markup_percent"
                      type="number"
                      min="0"
                      step="0.1"
                      inputMode="decimal"
                      placeholder="15"
                      value={data.markup_percent}
                      onChange={(e) => setData('markup_percent', e.target.value)}
                      className="h-9 w-32 text-[13px]"
                    />
                    <span className="text-[13px] text-muted-foreground">% of the buy rate</span>
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="markup_amount_usd" className="text-xs">Markup amount</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-[13px] text-muted-foreground">$</span>
                    <Input
                      id="markup_amount_usd"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="150"
                      value={data.markup_amount_usd}
                      onChange={(e) => setData('markup_amount_usd', e.target.value)}
                      className="h-9 w-32 text-[13px]"
                    />
                    <span className="text-[13px] text-muted-foreground">per container (USD)</span>
                  </div>
                </div>
              )}

              <div className="rounded-md bg-muted/40 p-3 text-[13px]">
                <span className="text-muted-foreground">Example: </span>
                a {formatUsd(EXAMPLE_BUY_USD)} buy rate is quoted at{' '}
                <span className="font-semibold text-foreground">{formatUsd(exampleSell)}</span>
                <span className="text-muted-foreground"> ({formatUsd(exampleMarkup)} markup) per container.</span>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Rate Preference
            </h2>
            <p className="mb-4 text-xs text-muted-foreground">
              When a contract (rate-sheet) rate and a live carrier rate both match a lane,
              which one ranks first in quote results.
            </p>
            <div className="inline-flex rounded-md border p-0.5">
              {([
                { value: 'prefer_contract', label: 'Prefer contract rates' },
                { value: 'cheapest', label: 'Cheapest first' },
              ] as const).map((option) => {
                const active = data.rate_preference === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setData('rate_preference', option.value)}
                    className={`cursor-pointer rounded px-3 py-1 text-[13px] font-medium transition-colors ${
                      active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
          </section>
        </form>
      </div>
    </>
  )
}

SettingsPricing.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
